import React, { useEffect, useMemo, useState } from 'react';
import { Form, DatePicker, Checkbox, Radio } from 'antd';
import {
  CustomUploadPhoto,
  FormInput,
  IconSmallArrow,
  OrangeButton,
  IconInfo,
  FileUpload,
  IconCalendar,
  IconClipboardType,
  TooltipButton,
} from 'components';
import classNames from 'classnames';
import { COLORS } from 'utils/colors';
import dayjs from 'dayjs';
import { createClient, getRequestData, updateUserImage, uploadDocument } from 'api/api';
import { endpoints } from 'api/endpoints';
import { DATE_FORMAT, ROLE_TYPES } from 'api/constants';
import { useNavigate } from 'react-router-dom';
import { useUser } from 'context/userContext';
import { useAuth } from 'context/authContext';
import {
  allowOnlyDigits,
  formatCalendarDateForBackend,
  getBackendCalendarDayjs,
  getEarliestAllowedStartDate,
} from 'utils/helpers';
import SlhSelect from 'components/slhSelect';

export const CreateClientByManager = () => {
  const navigate = useNavigate();
  const { userDetails, updateUserDetails } = useUser();
  const { isParentManaging, partnerId } = useAuth();
  const [btnDisabled, setBtnDisabled] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState([]);
  const [isMoveInTheSame, setIsMoveInTheSame] = useState(true);
  const [formTypes, setFormTypes] = useState([]);
  const [partnersList, setPartnersList] = useState([]);
  const [avatarImg, setAvatarImg] = useState(null);
  const [formData, setFormData] = useState({
    client: {
      firstname: '',
      lastname: '',
      email: '',
      password: '',
      confirm_password: '',
      role: 'client',
      gender: 'M',
      phone: '',
      country_code: '+1',
      pronoun: '',
      birthdate: '',
    },
    partner: {
      id: isParentManaging ? '' : userDetails?.partner_details?.id,
      approved: false,
      startDate: '',
      moveInDate: '',
    },
    tasks: [1],
  });
  useEffect(() => {
    (async () => {
      const { data } = await getRequestData(endpoints.getAllPartners(), ROLE_TYPES.slh_manager);

      if (data) {
        setPartnersList(data);
      }
    })();

    (async () => {
      const { data } = await getRequestData(endpoints.getForms, ROLE_TYPES.slh_manager);

      if (data) {
        const forms = data.map(({ ...rest }) => rest);
        setFormTypes(forms);
      }
    })();
  }, []);

  const doesPasswordsMatch = useMemo(
    () => formData.client.password === formData.client.confirm_password,
    [formData.client.password, formData.client.confirm_password],
  );

  const selectedPartnerSupportsDoer = useMemo(() => {
    if (!formData.partner.id) return true;
    const partner = partnersList.find((p) => String(p.id) === String(formData.partner.id));
    return partner?.doer ?? false;
  }, [formData.partner.id, partnersList]);

  const isSelectedSLHGenderMismatch = useMemo(() => {
    if (!formData.partner.id || !formData.client.gender) return false;
    const partner = partnersList.find((p) => String(p.id) === String(formData.partner.id));
    if (!partner?.gender) return false;
    const allowed = partner.gender.split(',').map((g) => g.trim());
    return !allowed.includes(formData.client.gender);
  }, [formData.partner.id, formData.client.gender, partnersList]);

  useEffect(() => {
    const isFormValid =
      !!formData.partner.startDate &&
      !!formData.partner.approved &&
      !!formData.client.birthdate &&
      !!formData.client.email &&
      !!formData.client.password &&
      !!formData.client.firstname &&
      !!formData.client.lastname &&
      doesPasswordsMatch;
    setBtnDisabled(!isFormValid);
  }, [
    formData.client.birthdate,
    formData.client.confirm_password,
    formData.client.email,
    formData.client.firstname,
    formData.partner.approved,
    formData.client.lastname,
    formData.client.password,
    formData.partner.startDate,
    doesPasswordsMatch,
  ]);

  useEffect(() => {
    if (isParentManaging) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        partner: {
          ...prevFormData.partner,
          id: partnerId,
        },
      }));
      return;
    }

    if (userDetails?.partner_details?.id) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        partner: {
          ...prevFormData.partner,
          id: userDetails.partner_details.id,
        },
      }));
    }
  }, [isParentManaging, partnerId, userDetails?.partner_details?.id]);

  const onValuesChange = (_, allValues) => {
    const { firstname, lastname, email, pronoun, password, confirm_password, phone } = allValues;

    setFormData({
      ...formData,
      client: {
        ...formData.client,
        firstname,
        lastname,
        email,
        pronoun,
        password,
        confirm_password,
        phone,
      },
    });
  };

  const handleImageUpload = (file) => {
    setAvatarImg(file);
  };

  const handleDocSelect = (files) => {
    setSelectedDoc(files || []);
  };

  const handleGenderChange = (e) => {
    const shortGender = e.target.value.slice(0, 1).toUpperCase();

    setFormData({
      ...formData,
      client: {
        ...formData.client,
        gender: shortGender,
      },
    });
  };

  const handleDateOfBornChange = (date) => {
    const birthdate = formatCalendarDateForBackend(date);
    setFormData({
      ...formData,
      client: {
        ...formData.client,
        birthdate,
      },
    });
  };
  const handleStartDateChange = (date) => {
    const startDate = formatCalendarDateForBackend(date);
    setFormData({
      ...formData,
      partner: {
        ...formData.partner,
        startDate,
        moveInDate: isMoveInTheSame ? startDate : formData.partner.moveInDate,
      },
    });
  };

  // const WEEK_DAYS = {
  //   sunday: 0,
  //   monday: 1,
  //   tuesday: 2,
  //   wednesday: 3,
  //   thursday: 4,
  //   friday: 5,
  //   saturday: 6,
  // };

  // const computePreviousFiscalDay = (fiscalDow) => {
  //   if (!fiscalDow) return null;
  //   const key = String(fiscalDow).toLowerCase();
  //   const fiscalNum = WEEK_DAYS[key] ?? 0;
  //   const today = dayjs().startOf('day');
  //   const diff = (today.day() - fiscalNum + 7) % 7; // days since last fiscal day (0 means today)
  //   return today.subtract(diff, 'day');
  // };
  const disabledStartDateCreate = (current) => {
    if (!current) return false;

    if (!formData.partner.id) {
      return false;
    }

    const partner = partnersList.find((p) => String(p.id) === String(formData.partner.id));
    const lastInvoiceDate = partner?.submittedLastInvoiceEndDate;
    if (lastInvoiceDate) {
      return current.startOf('day').valueOf() <= getBackendCalendarDayjs(lastInvoiceDate).valueOf();
    }

    const sixWeeksAgo = getEarliestAllowedStartDate();
    return current.startOf('day').isBefore(sixWeeksAgo);
  };

  const isSelectedStartDateOutOfAllowedRange =
    !!formData.partner.startDate &&
    !!formData.partner.id &&
    disabledStartDateCreate(dayjs(formData.partner.startDate));

  const handleMoveInDateChange = (date) => {
    const moveInDate = formatCalendarDateForBackend(date);
    setFormData({
      ...formData,
      partner: {
        ...formData.partner,
        moveInDate,
      },
    });
  };

  const handleMoveInTheSameChange = (e) => {
    const isChecked = e.target.checked;
    setIsMoveInTheSame(isChecked);

    setFormData({
      ...formData,
      partner: {
        ...formData.partner,
        moveInDate: isChecked ? formData.partner.startDate : formData.partner.moveInDate,
      },
    });
  };

  const handleSLHSelectChange = (value) => {
    const partner = partnersList.find((p) => String(p.id) === String(value));
    const supportsDoer = partner?.doer ?? false;

    setFormData({
      ...formData,
      partner: {
        ...formData.partner,
        id: value,
      },
      tasks: formData.tasks.includes(2) && !supportsDoer ? [1] : formData.tasks,
    });
  };

  const handleAcceptedAtSLHChange = (e) => {
    setFormData({
      ...formData,
      partner: {
        ...formData.partner,
        approved: e.target.checked,
      },
    });
  };

  const handleTaskChange = (e, task_id) => {
    const { tasks } = formData;
    const isChecked = e.target.checked;

    const updatedTasks = isChecked ? [task_id] : tasks.filter((id) => id !== task_id);
    setFormData((prevFormData) => ({
      ...prevFormData,
      tasks: updatedTasks,
    }));
  };

  const handleFormSubmit = () => {
    if (isSelectedStartDateOutOfAllowedRange) {
      return;
    }

    const { startDate } = formData.partner;

    const payload = {
      ...formData,
      partner: {
        ...formData.partner,
      },
    };

    if (isMoveInTheSame) {
      payload.partner.moveInDate = startDate;
    }

    createClient(payload, ROLE_TYPES.slh_manager, (data) => {
      const userID = data.user.id;
      selectedDoc.forEach((doc) => {
        uploadDocument(userID, doc, ROLE_TYPES.slh_manager);
      });
      avatarImg && updateUserImage(userID, avatarImg, ROLE_TYPES.slh_manager);
      navigate('/clients');
      updateUserDetails();
    });
  };

  const sanitizeDigitsOnInput = (e) => {
    const input = e.target;
    const rawValue = input.value;
    const digitsOnly = rawValue.replace(/\D/g, '');

    const prefixDigits = formData.client?.country_code.replace(/\D/g, '');
    const sanitized = digitsOnly.startsWith(prefixDigits)
      ? digitsOnly.slice(prefixDigits.length)
      : digitsOnly;

    if (rawValue !== sanitized) {
      input.value = sanitized;
    }
  };

  return (
    <Form
      name="create-client-by-admin"
      requiredMark={'optional'}
      className="flex flex-col w-full gap-6"
      onValuesChange={onValuesChange}
    >
      <div className="flex justify-between w-full">
        <div className="flex items-center gap-x-4">
          <div
            onClick={() => navigate(-1)}
            className="w-10 h-10 bg-white rounded-lg shadow-block-shadow flex items-center justify-center cursor-pointer"
          >
            <IconSmallArrow className="w-2 h-4" />
          </div>
          <p className="nunito-title">Add new Resident</p>
        </div>
        <OrangeButton onClick={handleFormSubmit} label="Save" disabled={btnDisabled} />
      </div>
      <div className="flex gap-6">
        <div className="flex flex-col gap-[18px] base-block pt-[30px] w-[442px]">
          <p className="nunito-title px-[30px] pb-[11px]">Resident Information</p>
          <div
            className={classNames(
              'relative w-full flex flex-col pt-14 pb-[30px] gap-y-[18px] border-t border-color-lightBlue px-[30px] self-center items-center',
              'before:content-[""] before:absolute before:w-4 before:h-4 before:rounded-full before:border before:border-color-lightBlue before:bg-white before:z-10 before:top-0 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
            )}
          >
            <CustomUploadPhoto className="self-center" onImageUpload={handleImageUpload} />
            <div className="flex gap-x-[18px]">
              <FormInput label="First Name*" name="firstname" placeholder="First Name" required />
              <FormInput label="Last Name*" name="lastname" placeholder="Last Name" required />
            </div>
            <div className="flex flex-col gap-y-1 w-full">
              <p className="text-14 font-medium">Date Of Birth*</p>
              <DatePicker
                allowClear={false}
                onChange={(_, date) => handleDateOfBornChange(date)}
                format={DATE_FORMAT}
                placeholder="Pick Date"
                className="h-[44px] text-base font-medium base-block rounded-lg "
                name="date-of-born"
                suffixIcon={<IconCalendar color={COLORS.blue} />}
              />
            </div>
            <FormInput
              className="w-full"
              label="Email*"
              name="email"
              placeholder="Email"
              required
            />
            <div className="flex flex-col">
              <div className="flex gap-x-[18px]">
                <FormInput
                  type="password"
                  label="Password*"
                  name="password"
                  placeholder="Password"
                  required
                />
                <FormInput
                  type="password"
                  label="Confirm Password*"
                  name="confirm_password"
                  placeholder="Password"
                  required
                />
              </div>
              {!doesPasswordsMatch &&
                formData.client.password &&
                formData.client.confirm_password && (
                  <p className="text-red text-sm mt-1">Passwords do not match</p>
                )}
            </div>
            <div className="flex flex-col w-full gap-y-1">
              <p>Gender*</p>
              <Radio.Group
                onChange={(e) => handleGenderChange(e)}
                className="gap-y-4"
                name="gender"
                defaultValue={'Male'}
              >
                <Radio
                  className="px-4 py-2.5 bg-white border border-color-divider rounded-xl "
                  value={'Male'}
                >
                  Male
                </Radio>
                <Radio
                  className="px-4 py-2.5 bg-white border border-color-divider rounded-xl "
                  value={'Female'}
                >
                  Female
                </Radio>
              </Radio.Group>
            </div>
            <FormInput
              className="w-full"
              label="Phone"
              type="text"
              pattern="['0-9']*"
              maxLength={10}
              name="phone"
              placeholder="Phone"
              prefix={formData.client.country_code}
              onKeyDown={allowOnlyDigits}
              onWheel={(e) => {
                e.target.blur();
                e.stopPropagation();

                setTimeout(() => {
                  e.target.focus();
                }, 0);
              }}
              onInput={sanitizeDigitsOnInput}
            />
            <div className="flex flex-col gap-y-1 w-full">
              <p className="text-14 font-medium">Document</p>
              <FileUpload className="w-full" onFileSelect={handleDocSelect} />
            </div>
            <div className="flex flex-col w-full gap-y-1">
              <p className="text-14 font-medium">SLH*</p>
              <SlhSelect
                data={partnersList}
                value={formData.partner.id}
                onChange={(id) => handleSLHSelectChange(id)}
                placement="top"
                residentGender={formData.client.gender}
              />
              {isSelectedSLHGenderMismatch && (
                <p className="text-12 text-red mt-1">Gender mismatch for selected SLH</p>
              )}
              <div className="flex justify-between mt-1">
                <Checkbox
                  onChange={handleAcceptedAtSLHChange}
                  name="accepted-slh"
                  className="flex items-center"
                >
                  <p className="text-16 ml-2.5">Accepted at SLH*</p>
                </Checkbox>
                <TooltipButton
                  icon={<IconInfo color={COLORS.blue} />}
                  text="Info"
                  tooltipText="Residents must have already applied for entry to a partner SLH and been accepted for a term of residency at the SLH."
                />
              </div>
            </div>
            <div className="flex flex-col gap-y-1 w-full">
              <p className="text-14 font-medium">Scholarship Start Date*</p>
              <DatePicker
                allowClear={false}
                onChange={(_, date) => handleStartDateChange(date)}
                format={DATE_FORMAT}
                placeholder="Pick Date"
                className="h-[44px] text-base font-medium base-block rounded-lg"
                name="date-of-start"
                suffixIcon={<IconCalendar color={COLORS.blue} />}
                disabledDate={disabledStartDateCreate}
              />
              {isSelectedStartDateOutOfAllowedRange && (
                <p className="text-12 text-red mt-1">Selected Date is out of allowed range</p>
              )}
            </div>
            <div className="flex w-full">
              <div className="flex flex-col w-full gap-y-1">
                <p className="text-14 font-medium">Move-in Date*</p>
                <div className="flex w-full gap-x-3">
                  <DatePicker
                    allowClear={false}
                    onChange={(_, date) => handleMoveInDateChange(date)}
                    format={DATE_FORMAT}
                    value={formData.partner.moveInDate ? dayjs(formData.partner.moveInDate) : null}
                    placeholder="Pick Date"
                    disabled={isMoveInTheSame}
                    className="h-[44px] text-base font-medium base-block rounded-lg"
                    name="date-of-move-in"
                    suffixIcon={<IconCalendar color={COLORS.blue} />}
                  />
                  <Checkbox
                    onChange={handleMoveInTheSameChange}
                    name="same-as-start-date"
                    className="flex items-center !bg-transparent flex-1"
                    checked={isMoveInTheSame}
                  >
                    <p className="text-16 ml-2.5">Same as start</p>
                  </Checkbox>
                </div>
              </div>
            </div>
            <FormInput
              className="w-full"
              label="Identify As"
              name="identify"
              placeholder="Identify as"
            />
          </div>
        </div>
        <div className="flex flex-col base-block pt-[30px] flex-1">
          <div className="flex justify-between px-[30px] pb-[17px]">
            <p className="nunito-title">FORMS</p>
            {/* <Input
              className='search w-[300px] text-lightBlue text-16 rounded-ten bg-light'
              type='text'
              placeholder="Search"
              addonBefore={<IconSearch color={COLORS.lightBlue} />}
              onSearch={onSearch}
            /> */}
          </div>
          {formTypes.length > 0 && (
            <div
              className={classNames(
                'relative w-full flex flex-col pt-6 pb-[30px] border-t border-color-lightBlue px-[30px] self-center items-center',
                'before:content-[""] before:absolute before:w-4 before:h-4 before:rounded-full before:border before:border-color-lightBlue before:bg-white before:z-10 before:top-0 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
              )}
            >
              <div className="flex justify-between items-center bg-white shadow-block-shadow rounded-lg w-full p-4 gap-x-4">
                <Checkbox
                  name="weekly-form"
                  className="weekly-forms !bg-transparent"
                  checked={formData.tasks.includes(1)}
                  onChange={(e) => handleTaskChange(e, 1)}
                >
                  <div className="flex items-center">
                    <IconClipboardType className="ml-2.5 mr-1 min-w-6 min-h-6" />
                    <p className="text-16">{formTypes[0]?.name}*</p>
                  </div>
                </Checkbox>
                <TooltipButton
                  icon={<IconInfo color={COLORS.blue} />}
                  text="Info"
                  tooltipText="This is weekly summary form that resident will get at the completion of the week during his stay in SLH."
                />
              </div>
              <div className="flex flex-col mt-6 pt-6 border-t border-color-divider w-full">
                <p className="text-14 mb-1">Additional Form</p>
                {formTypes.map(({ name }) => {
                  return (
                    <div
                      className="flex flex-col w-full mb-6"
                      key={name}
                    >
                      <div className="flex justify-between items-center bg-white shadow-block-shadow rounded-lg w-full p-4 gap-x-4">
                        <Checkbox
                          name="weekly-form"
                          className="weekly-forms !bg-transparent"
                          checked={formData.tasks.includes(2)}
                          disabled={!selectedPartnerSupportsDoer}
                          onChange={(e) => handleTaskChange(e, 2)}
                        >
                          <div className="flex items-center">
                            <IconClipboardType className="ml-2.5 mr-1 min-w-6 min-h-6" />
                            <p className="text-16">Impact of Dogs on Well-Being in Sober Homes</p>
                          </div>
                        </Checkbox>
                        <TooltipButton
                          icon={<IconInfo color={COLORS.blue} />}
                          text="Info"
                          tooltipText="The resident will get changed Weekly Summary Form that will cover also DOER related questions."
                        />
                      </div>
                      {!selectedPartnerSupportsDoer && formData.partner.id && (
                        <p className="text-12 text-grey mt-1 pl-1">
                          Selected SLH doesn&apos;t support the DOER program.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </Form>
  );
};
